import React, { useEffect } from "react";
import QuestionAnswer from "./QuestionAnswer";
import Askquestion from "./Askquestion";
import { useBaseChatLogic } from "./BaseChatComponent";
import type { ChatMessage } from "./BaseChatComponent";
import { useLocation } from "react-router-dom";

interface CallChatComponentProps {
  showWideSidebar: boolean;
  showHistory: boolean;
  onOpenDocumentClick: () => void;
  activeMode: "support" | "call";
  setActiveMode: (mode: "support" | "call") => void;
}

export default function CallChatComponent({
  showWideSidebar,
  showHistory,
  onOpenDocumentClick,
  activeMode,
  setActiveMode,
}: CallChatComponentProps) {
  const {
    showQuestionAnswerBlock,
    setShowQuestionAnswerBlock,
    messages,
    setMessages,
    isLoading,
    setIsLoading,
    conversationId,
    setConversationId,
    paramConversationId,
    handleFollowUpQuestion,
    handlePromptClick,
  } = useBaseChatLogic();

  const fetchCallChatHistory = async (transactionId: string) => {
    try {
      const token = localStorage.getItem("igtoken");
      const response = await fetch(
        `${process.env.REACT_APP_Alexandria_API}/api/Alexandria/callhistory/${transactionId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      const chatHistory: ChatMessage[] = data.map((item: any) => ({
        question: item.question,
        answer: item.response,
        documents: [],
        sources: [],
        chatId: String(item.callId),
        conversationId: item.transactionId,
      }));

      setMessages(chatHistory);
      setShowQuestionAnswerBlock(true);
    } catch (error) {
      console.error("Error fetching call history:", error);
    }
  };

  useEffect(() => {
    if (paramConversationId) {
      setConversationId(paramConversationId);
      fetchCallChatHistory(paramConversationId);
    }
  }, [paramConversationId, setConversationId]);

  const handleSendEmbeddingMessage = async (message: string) => {
    if (!message?.trim()) return;

    setIsLoading(true);
    setShowQuestionAnswerBlock(true);

    const newMessage = {
      question: message,
      answer: "",
      documents: [],
      sources: [],
      conversationId: conversationId,
    };

    setMessages((prev) => [...prev, newMessage]);

    let retryCount = 0;
    const maxRetries = 3;

    while (retryCount < maxRetries) {
      try {
        const token = localStorage.getItem("igtoken");
        if (!token) throw new Error("Missing auth token");

        const formData = new FormData();
        formData.append("ConversationId", conversationId);
        formData.append("question", message);

        const response = await fetch(
          `https://alexandriaai-stage.insurancegig.com/api/alexandria-ai/embeddings`,
          {
            method: "POST",
            headers: { authorization: `Bearer ${token}` },
            body: formData,
          }
        );

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const contentType = response.headers.get("content-type") || "";

        if (
          !contentType.toLowerCase().includes("event-stream") &&
          !contentType.toLowerCase().includes("stream") &&
          !contentType.toLowerCase().includes("octet-stream")
        ) {
          const jsonResponse = await response.json();
          setMessages((prev) => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            lastMessage.answer =
              jsonResponse.answer ||
              jsonResponse.Answer ||
              jsonResponse.response ||
              jsonResponse.text ||
              jsonResponse.content ||
              "No response received";
            lastMessage.documents =
              jsonResponse.documents || jsonResponse.Documents || [];
            lastMessage.sources =
              jsonResponse.sources || jsonResponse.Sources || [];
            lastMessage.chatId = jsonResponse.chatId || jsonResponse.ChatId;
            return newMessages;
          });
          break;
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No reader available");

        let buffer = "";
        let answer = "";
        let lastLength = 0;
        let documents: string[] = [];
        let sources: string[] = [];
        let chatId: string | undefined;
        let hasReceivedData = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += new TextDecoder().decode(value, { stream: true });
          let lines = buffer.split("\n");
          buffer = lines.pop() || ""; 

          for (const line of lines) {
            if (!line.trim()) continue;
            if (line.startsWith("data: ")) {
              const jsonStr = line.slice(6).trim();
              if (!jsonStr || jsonStr === "[DONE]") continue;

              try {
                const data = JSON.parse(jsonStr);
                hasReceivedData = true;

                const answerField =
                  data.Answer ||
                  data.answer ||
                  data.response ||
                  data.text ||
                  data.content;
                if (answerField) {
                  const cleanAnswer = answerField.replace(
                    /\$\$PARENTDOMIDSTART/g,
                    ""
                  );
                  const newPart = cleanAnswer.slice(lastLength);
                  lastLength = cleanAnswer.length;
                  if (newPart) {
                    for (let i = 0; i < newPart.length; i++) {
                      await new Promise((resolve) => setTimeout(resolve, 10));
                      answer += newPart[i];
                      setMessages((prev) => {
                        const newMessages = [...prev];
                        newMessages[newMessages.length - 1].answer = answer;
                        return newMessages;
                      });
                    }
                  }
                }

                if (data.Documents || data.documents) {
                  documents = Array.isArray(data.Documents || data.documents)
                    ? data.Documents || data.documents
                    : [data.Documents || data.documents];
                  setMessages((prev) => {
                    const newMessages = [...prev];
                    newMessages[newMessages.length - 1].documents = documents;
                    return newMessages;
                  });
                }

                if (data.Sources || data.sources) {
                  sources = Array.isArray(data.Sources || data.sources)
                    ? data.Sources || data.sources
                    : [data.Sources || data.sources];
                  setMessages((prev) => {
                    const newMessages = [...prev];
                    newMessages[newMessages.length - 1].sources = sources;
                    return newMessages;
                  });
                }

                if (data.ChatId || data.chatId) {
                  chatId = data.ChatId || data.chatId;
                  setMessages((prev) => {
                    const newMessages = [...prev];
                    newMessages[newMessages.length - 1].chatId = chatId;
                    return newMessages;
                  });
                }
              } catch (e) {
                console.error("Error parsing event data:", e, line);
              }
            }
          }
        }

        if (!hasReceivedData) {
          setMessages((prev) => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            lastMessage.answer =
              "I received your question but couldn't generate a proper response.";
            return newMessages;
          });
        }

        break; 
      } catch (error) {
        console.error("Error in request:", error);
        retryCount++;
        if (retryCount < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          continue;
        }
        setMessages((prev) => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          lastMessage.answer =
            "I apologize, but I encountered an error while processing your request.";
          return newMessages;
        });
      }
    }

    setIsLoading(false);
  };

  const callPromptCards = [
    { label: "Call Analytics", title: "What all overall problems are customers facing?" },
    { label: "Call Insights", title: "Provide some of the customer complaints?" },
    { label: "Call Reports", title: "Give me the list of complaints from customer?" },
  ];

const location = useLocation();

useEffect(() => {
  if (location.state?.category) {
    setActiveMode(location.state.category);
  }
}, [location.state?.category]);


  return (
    <>
      {showQuestionAnswerBlock ? (
        <div
          className={showWideSidebar ? "chat-question-large-width" : "chat-question-small-width"}
        >
          <QuestionAnswer
            messages={messages}
            onOpenDocumentClick={onOpenDocumentClick}
            onFollowUpQuestionClick={(question) =>
              handleFollowUpQuestion(question, handleSendEmbeddingMessage)
            }
            category="call"
          />
        </div>
      ) : (
        <>
          <div className="text-center my-4 mb-5 mt-5">
            <h5 className="fw-semibold mb-3">
              Call Analytics - What can I help with?
            </h5>
            <p className="text-muted small">
              Welcome to Call Analytics! Explore insights from your call data.
            </p>
          </div>

          <div className="container my-5 mt-5 mb-5">
            <div className="row justify-content-center g-1 mt-5 mb-5">
              {callPromptCards.map((item, idx) => (
                <div
                  key={idx}
                  className="col-12 col-sm-6 col-md-3 d-flex justify-content-center"
                >
                  <div
                    className="prompt-card d-flex flex-column justify-content-center"
                    onClick={() =>
                      handlePromptClick(item.title, handleSendEmbeddingMessage)
                    }
                    style={{
                      cursor: "pointer",
                      width: "100%",
                      maxWidth: "300px",
                    }}
                  >
                    <div>
                      <span className="badge">{item.label}</span>
                      <div className="card-title">{item.title}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div
        className={`mt-auto pt-4 ${
          showWideSidebar
            ? "chat-question-large-block"
            : "chat-question-small-block"
        }`}
      >
        <div
          className={`${
            showHistory
              ? "main-historycontent-large-width"
              : "main-historycontent-small-width"
          }`}
        >
          <div className="px-3 py-1">
            <Askquestion
              onSendMessage={handleSendEmbeddingMessage}
              isLoading={isLoading}
            />
          </div>

          <small className="text-muted d-block text-center text-12">
            AI-generated content may be incorrect
          </small>
          <div className="d-flex justify-content-center my-2 gap-2">
            <button
              className={`custom-btn btn-sm ${
                activeMode === "support"
                  ? "active-support"
                  : "outline-support"
              }`}
              onClick={() => setActiveMode("support")}
            >
              Support
            </button>
            <button
              className={`custom-btn btn-sm ${
                activeMode === "call" ? "active-call" : "outline-call"
              }`}
              onClick={() => setActiveMode("call")}
            >
              Call
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
